import { useEffect, useState } from "react";
import { Cpu, Plus, Trash2, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Machines are persisted as the `machines` setting — a JSON array [{name, cost}].
// `cost` is the ₹/pc that auto-fills a Process Cost row when that machine is picked.
// On save we also mirror the names into `machine_tonnages` (comma-separated) so the
// existing tonnage dropdown keeps working everywhere.
export default function Machines() {
  const [items, setItems] = useState([]);   // [{name, cost}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCost, setNewCost] = useState("");

  async function load() {
    setLoading(true);
    try {
      // Admin GET /settings returns an ARRAY of {key, value} rows.
      let machinesRaw = null, tonnagesRaw = "";
      try {
        const r = await api.get("/settings");
        const rows = r.data || [];
        machinesRaw = rows.find((x) => x.key === "machines")?.value ?? null;
        tonnagesRaw = rows.find((x) => x.key === "machine_tonnages")?.value ?? "";
      } catch {
        const r = await api.get("/settings/public");
        machinesRaw = r.data?.machines ?? null;
        tonnagesRaw = r.data?.machine_tonnages ?? "";
      }
      let list = [];
      if (machinesRaw) {
        try { list = JSON.parse(machinesRaw); } catch { list = []; }
      }
      if (!Array.isArray(list) || list.length === 0) {
        // First run: seed from the legacy comma-separated tonnage names (cost 0).
        list = String(tonnagesRaw || "").split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ name, cost: 0 }));
      }
      setItems(list.map((m) => ({ name: String(m.name || ""), cost: Number(m.cost) || 0 })));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const addItem = () => {
    const n = newName.trim();
    if (!n) return;
    if (items.some((x) => (x.name || "").toLowerCase() === n.toLowerCase())) { toast.error("That machine is already in the list"); return; }
    setItems((p) => [...p, { name: n, cost: Number(newCost) || 0 }]);
    setNewName(""); setNewCost("");
  };
  const updateItem = (i, patch) => setItems((p) => p.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    setItems((p) => { const a = [...p]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  };

  const save = async () => {
    const cleaned = items.map((m) => ({ name: (m.name || "").trim(), cost: Number(m.cost) || 0 })).filter((m) => m.name);
    const seen = new Set();
    const uniq = cleaned.filter((m) => { const k = m.name.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
    setSaving(true);
    try {
      await api.put("/settings/machines", { value: JSON.stringify(uniq) });
      // Keep the legacy tonnage-name list in sync so the dropdown source stays valid.
      await api.put("/settings/machine_tonnages", { value: uniq.map((m) => m.name).join(", ") });
      setItems(uniq);
      toast.success("Machines saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-3xl mx-auto" data-testid="machines-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Cpu size={22} className="text-orange-600" /> Machines
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Internal machine / tonnage list with cost. The <strong>Cost (₹/pc)</strong> auto-fills a Process Cost row when that machine is selected. Never printed on a customer quote or Excel.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
          data-testid="machines-save"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        {/* Add row */}
        <div className="flex items-center gap-2 mb-4">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
            placeholder="Machine name, e.g. 63T, 200T, Wirecut, Tapping…"
            className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm"
            data-testid="machines-new-input"
          />
          <div className="relative w-32">
            <span className="absolute left-2 top-2 text-xs text-slate-400 pointer-events-none">₹</span>
            <input
              type="number" step="0.01" min="0"
              value={newCost}
              onChange={(e) => setNewCost(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder="Cost/pc"
              className="w-full border border-slate-200 rounded pl-5 pr-2 py-2 text-sm text-right"
              data-testid="machines-new-cost"
            />
          </div>
          <button
            onClick={addItem}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-orange-300 text-orange-700 rounded hover:bg-orange-50"
            data-testid="machines-add"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">No machines yet. Add one above.</div>
        ) : (
          <div className="border border-slate-100 rounded-md overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <span className="w-12" />
              <span className="flex-1">Machine</span>
              <span className="w-32 text-right pr-2">Cost (₹/pc)</span>
              <span className="w-7" />
            </div>
            <ul className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`machine-row-${i}`}>
                  <span className="text-slate-300 flex flex-col leading-none">
                    <button onClick={() => move(i, i - 1)} className="hover:text-slate-600" title="Move up">▲</button>
                    <button onClick={() => move(i, i + 1)} className="hover:text-slate-600" title="Move down">▼</button>
                  </span>
                  <GripVertical size={14} className="text-slate-300" />
                  <input
                    value={it.name}
                    onChange={(e) => updateItem(i, { name: e.target.value })}
                    className="flex-1 border border-slate-200 rounded px-2 py-1 text-sm font-mono"
                    data-testid={`machine-input-${i}`}
                  />
                  <div className="relative w-32">
                    <span className="absolute left-2 top-1.5 text-xs text-slate-400 pointer-events-none">₹</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={it.cost}
                      onChange={(e) => updateItem(i, { cost: e.target.value })}
                      className="w-full border border-slate-200 rounded pl-5 pr-2 py-1 text-sm text-right font-mono"
                      data-testid={`machine-cost-${i}`}
                    />
                  </div>
                  <button
                    onClick={() => removeItem(i)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    title="Remove"
                    data-testid={`machine-delete-${i}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="text-[11px] text-slate-400 mt-3">
          {items.length} machine{items.length === 1 ? "" : "s"}. Set a Cost (₹/pc) to auto-fill the Process Cost row when the machine is chosen. Changes apply after you click <strong>Save</strong>.
        </div>
      </div>

    </div>
  );
}
